import './CoralCardsSlider.scss'

import React, { ReactNode, useEffect, useRef, useState } from 'react'

import { DownArrowIcon } from '../../../../coral-library/icons/DownArrow.icon'

type CoralCardsSliderProps = {
  className?: string
  children: ReactNode
  forceRow?: boolean
  fullExpand?: boolean
  belowSliderContent?: ReactNode
}

// eslint-disable-next-line max-lines-per-function
export const CoralCardsSlider: React.FC<CoralCardsSliderProps> = ({
  className,
  children,
  forceRow,
  fullExpand,
  belowSliderContent,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [paddingInline, setPaddingInline] = useState(0)

  const [showNav, setShowNav] = useState(false)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [isInView, setIsInView] = useState(false)

  const findPaddedAncestor = (el: HTMLElement | null): number => {
    while (el) {
      const style = window.getComputedStyle(el)
      const left = parseFloat(style.paddingLeft) || 0
      const right = parseFloat(style.paddingRight) || 0

      if (left > 0 || right > 0) {
        return Math.max(left, right)
      }

      el = el.parentElement
    }
    return 0
  }

  useEffect(() => {
    const updatePadding = () => {
      const wrapper = wrapperRef.current
      if (!wrapper) return
      const padding = findPaddedAncestor(wrapper.parentElement)
      setPaddingInline(padding)
    }

    updatePadding()
    window.addEventListener('resize', updatePadding)
    return () => window.removeEventListener('resize', updatePadding)
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateNav = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container
      setShowNav(scrollWidth > clientWidth && container.childElementCount > 1)
      setCanScrollLeft(scrollLeft > 5)
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5)
    }

    updateNav()
    container.addEventListener('scroll', updateNav)
    window.addEventListener('resize', updateNav)
    return () => {
      container.removeEventListener('scroll', updateNav)
      window.removeEventListener('resize', updateNav)
    }
  }, [children])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isInView) return
      if (e.key === 'ArrowLeft' && canScrollLeft) scrollToNext('left')
      else if (e.key === 'ArrowRight' && canScrollRight) scrollToNext('right')
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [canScrollLeft, canScrollRight, isInView])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting)
      },
      {
        root: null,
        threshold: 0.1,
      }
    )
    const current = wrapperRef.current
    if (current) observer.observe(current)
    return () => {
      if (current) {
        observer.unobserve(current)
      }
    }
  }, [])
  const scrollToNext = (direction: 'left' | 'right') => {
    const container = containerRef.current
    if (!container) return
    const childrenArray = Array.from(container.children) as HTMLElement[]
    const containerRect = container.getBoundingClientRect()

    if (direction === 'right') {
      const containerRight = containerRect.right
      for (let i = 0; i < childrenArray.length; i++) {
        const child = childrenArray[i]
        const rect = child.getBoundingClientRect()
        const visibleWidth = Math.min(containerRight - rect.left, rect.width)
        const visibilityPercentage = (visibleWidth / rect.width) * 100
        if (
          (rect.right > containerRight + 1 && visibilityPercentage < 60) ||
          i === childrenArray.length - 1
        ) {
          if (i === childrenArray.length - 1) {
            container.scrollTo({
              left: container.scrollWidth - container.clientWidth,
              behavior: 'smooth',
            })
          } else {
            const offset = rect.left - containerRect.left
            container.scrollBy({ left: offset - 24, behavior: 'smooth' })
          }
          break
        }
      }
    } else {
      for (let i = childrenArray.length - 1; i >= 0; i--) {
        const child = childrenArray[i]
        const rect = child.getBoundingClientRect()
        const visibleWidth = Math.min(rect.right - containerRect.left, rect.width)
        const visibilityPercentage = (visibleWidth / rect.width) * 100
        if ((rect.left < containerRect.left - 1 && visibilityPercentage < 60) || i === 0) {
          if (i === 0) {
            container.scrollTo({
              left: 0,
              behavior: 'smooth',
            })
          } else {
            const offset = rect.left - containerRect.left
            container.scrollBy({ left: offset - 24, behavior: 'smooth' })
          }
          break
        }
      }
    }
  }

  return (
    <div className={`coral-card-slider ${className || ''}`} ref={wrapperRef}>
      <div
        className={`coral-card-slider__track${forceRow ? ' m--row' : ''} ${
          fullExpand ? ' m--full-expand' : ''
        }`}
        ref={containerRef}
        style={{
          marginInline: `-${paddingInline}px`,
          paddingInline: `${paddingInline}px`,
          scrollPaddingInline: `${paddingInline}px`,
        }}
      >
        {children}
      </div>
      {belowSliderContent && belowSliderContent}
      {showNav && (
        <div className={'coral-card-slider__navigation'}>
          <button
            aria-label={'scroll left'}
            className={
              'coral-global-button m--type-fill m--padding-0   m--theme-white coral-card-slider__button-override'
            }
            disabled={!canScrollLeft}
            onClick={() => scrollToNext('left')}
          >
            <DownArrowIcon direction={'left'} size={24} />
          </button>
          <button
            aria-label={'scroll right'}
            className={
              'coral-global-button m--padding-0 m--type-fill m--theme-white coral-card-slider__button-override'
            }
            disabled={!canScrollRight}
            onClick={() => scrollToNext('right')}
          >
            <DownArrowIcon direction={'right'} size={24} />
          </button>
        </div>
      )}
    </div>
  )
}
